/** Shared confirmed deletion control; completion is the durable receipt, never acceptance. */
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { StudyAuthoringState, StudyAuthoringDeletionReceipt } from '@testfeed/shopify-contracts'
import type { ShopifyAppBridge } from '../../shopifyApiFetch'
import { useShopifyAppContext } from '../../ShopifyAppContext'
import { useShopifyScopeLifecycle } from '../../hooks/useShopifyScopeLifecycle'
import { runStudyAuthoringDeletion } from './runStudyAuthoringDeletion'
import { MerchantFacingError, resolveMerchantErrorMessage } from '../../merchantErrors'
import { StudySetupDeleteDialog } from '../StudySetupDeleteDialog'
import type { ActionResult } from '../../hooks/usePageLifecycle'
import { trackShopifyAction } from '../../shopifyUiTracking'
type DeletionLifecycle = {
  run: <T>(action: 'delete-setup', work: (signal: AbortSignal) => Promise<T>) => Promise<ActionResult<T>>
}
export function DirectStudyDeleteControl({
  shopify,
  studyId,
  state,
  receipt,
  title,
  onDeleted,
  lifecycle,
  disabled = false,
  label,
}: {
  shopify: ShopifyAppBridge
  studyId: string
  state?: StudyAuthoringState | null
  receipt?: StudyAuthoringDeletionReceipt
  title: string
  onDeleted: () => void
  lifecycle?: DeletionLifecycle
  disabled?: boolean
  label?: string
}) {
  const client = useQueryClient()
  const scope = useShopifyAppContext()
  const active = useShopifyScopeLifecycle(`${scope.shopDomain}:${scope.orgId}:${scope.connectionId}:${studyId}`)
  const [open, setOpen] = useState(false)
  const [operationId, setOperationId] = useState<string | null | undefined>(undefined)
  const durableDeletion = receipt ?? state?.deletion
  const resumedOperation =
    durableDeletion && durableDeletion.status !== 'failed' && durableDeletion.status !== 'completed'
      ? durableDeletion.operationId
      : null
  const pendingOperation = operationId === undefined ? resumedOperation : operationId
  const mutation = useMutation({
    mutationFn: async () => {
      const token = active.token
      const work = async (signal?: AbortSignal) => {
        const outcome = await runStudyAuthoringDeletion({
          shopify,
          studyId,
          operationId: pendingOperation,
          signal,
          isActive: () => active.isActive(token),
          onOperationId: setOperationId,
          onReceipt: (receipt) => {
            setOperationId(receipt.status === 'failed' ? null : receipt.operationId)
            if (receipt.status === 'failed') {
              void client.invalidateQueries({
                queryKey: ['shopify-study-authoring', scope.shopDomain, scope.orgId, scope.connectionId],
              })
            }
          },
        })
        if (outcome.status === 'pending')
          throw new MerchantFacingError('Deletion is still in progress. Reopen Tests to check its status.', {
            code: 'STUDY_DELETION_IN_PROGRESS',
          })
        return
      }
      if (lifecycle) {
        const result = await lifecycle.run('delete-setup', work)
        if (result.status === 'failed') throw result.cause
        if (result.status !== 'done') throw new DOMException('Deletion cancelled', 'AbortError')
      } else await trackShopifyAction(shopify, 'delete-setup', work)
      if (!active.isActive(token)) return
      await client.invalidateQueries({
        queryKey: ['shopify-study-authoring', scope.shopDomain, scope.orgId, scope.connectionId],
      })
      if (!active.isActive(token)) return
      shopify.toast?.show('Test deleted')
      onDeleted()
    },
  })
  const visible = receipt?.status === 'failed' || state?.canDelete
  if (!visible && !pendingOperation) return null
  const error = mutation.error
    ? resolveMerchantErrorMessage(mutation.error, 'The test could not be deleted. Try again.')
    : null
  const actionLabel = pendingOperation ? 'Check deletion' : (label ?? 'Delete test')
  const onAction = () => {
    if (pendingOperation) mutation.mutate()
    else trackShopifyAction(shopify, 'confirm-delete', () => setOpen(true))
  }
  return (
    <>
      <s-button
        slot="secondary-actions"
        tone="critical"
        disabled={disabled || mutation.isPending}
        loading={mutation.isPending}
        onClick={onAction}
      >
        {actionLabel}
      </s-button>
      {!open && error && <s-banner tone="critical">{error}</s-banner>}
      <StudySetupDeleteDialog
        open={open}
        productTitle={title}
        isDeleting={mutation.isPending}
        errorMessage={error}
        onCancel={() => {
          if (!mutation.isPending) trackShopifyAction(shopify, 'cancel-delete', () => setOpen(false))
        }}
        onConfirm={() => mutation.mutate()}
      />
    </>
  )
}
